"use client";

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Database, Download, HardDriveUpload, Image as ImageIcon, Pencil, Search, Trash2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { Button, ListCount, ListSurface, ListSurfaceBody, ListSurfaceFooter, ListSurfaceHeader, ListToolbar, ListToolbarGroup, Modal, SkeletonTableRow } from "@/components/ds";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { FlashToast } from "@/components/ui-system/flash-toast";
import {
  deleteStorageArtifacts,
  deleteStorageRows,
  exportDeleteStorageRows,
  getMyDataUsage,
  getStorageItemDetail,
  listStorageItems,
  listSuperAdminAdminStorage,
  type StorageCleanupRequestDto,
  type StorageItemSummaryDto,
  type SuperAdminAdminStorageRowDto,
} from "@/lib/api/endpoints/data-management";
import { updateUser } from "@/lib/api/endpoints/users";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { isSuperAdmin } from "@/modules/auth/permissions";
import { useAuthStore } from "@/modules/auth/store";

function formatBytes(value: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  for (const unit of units) {
    if (size < 1024 || unit === units[units.length - 1]) {
      return unit === "B" ? `${Math.round(size)} B` : `${size.toFixed(2)} ${unit}`;
    }
    size /= 1024;
  }
  return `${value} B`;
}

function actionIcon(itemKey: string) {
  if (itemKey === "ota_files") return <HardDriveUpload className="h-4 w-4" aria-hidden />;
  if (itemKey === "media") return <ImageIcon className="h-4 w-4" aria-hidden />;
  return <Database className="h-4 w-4" aria-hidden />;
}

function entryId(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function quotaTone(status: string): string {
  if (status === "locked") return "inline-flex rounded-control border border-error/25 bg-error/10 px-2.5 py-1 text-micro font-semibold text-error";
  if (status === "warning") return "inline-flex rounded-control border border-warning/25 bg-warning/10 px-2.5 py-1 text-micro font-semibold text-warning";
  return "inline-flex rounded-control border border-success/25 bg-success/10 px-2.5 py-1 text-micro font-semibold text-success";
}

export function DataManagementPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = useEffectivePermissions();
  const [toast, setToast] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<StorageItemSummaryDto | null>(null);
  const [cleanupMode, setCleanupMode] = useState<StorageCleanupRequestDto["mode"]>("before_date");
  const [beforeDate, setBeforeDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [recordCount, setRecordCount] = useState("1000");
  const [selectedArtifactIds, setSelectedArtifactIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") ?? "all");
  const [editingAdmin, setEditingAdmin] = useState<SuperAdminAdminStorageRowDto | null>(null);
  const [storageLimitDraft, setStorageLimitDraft] = useState("");
  const canDeleteStorage = hasModulePermission(user, permissions.permissionMap, "data_management", "delete");
  const canExportStorage = hasModulePermission(user, permissions.permissionMap, "data_management", "export");
  const canManageStorage = canDeleteStorage;
  const superAdmin = isSuperAdmin(user);
  const managedTenantId = superAdmin ? searchParams.get("tenant_id") : null;
  const managedAdminUserId = superAdmin ? searchParams.get("admin_user_id") : null;
  const isSuperAdminListMode = superAdmin && !managedTenantId;
  const pageSize = 10;

  const usageQuery = useQuery({
    queryKey: ["data-management", "usage", "me", managedTenantId ?? "self", managedAdminUserId ?? "self"],
    queryFn: () => getMyDataUsage(managedTenantId, managedAdminUserId),
    enabled: !isSuperAdminListMode,
    staleTime: 30_000,
  });
  const superAdminListQuery = useQuery({
    queryKey: ["data-management", "super-admin", "admin-storage", search, statusFilter, page],
    queryFn: () => listSuperAdminAdminStorage({
      search,
      status: statusFilter === "all" ? null : statusFilter,
      limit: pageSize,
      offset: page * pageSize,
    }),
    enabled: isSuperAdminListMode,
    staleTime: 30_000,
  });
  const itemsQuery = useQuery({
    queryKey: ["data-management", "storage-items", managedTenantId ?? "self"],
    queryFn: () => listStorageItems(managedTenantId),
    enabled: Boolean(user?.id) && !isSuperAdminListMode,
    staleTime: 30_000,
  });
  const detailQuery = useQuery({
    queryKey: ["data-management", "storage-item", selectedItem?.key ?? "none", managedTenantId ?? "self"],
    queryFn: () => getStorageItemDetail(selectedItem!.key, managedTenantId),
    enabled: Boolean(selectedItem && canManageStorage && !isSuperAdminListMode),
    staleTime: 15_000,
  });

  const invalidateAll = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["data-management"] }),
      qc.invalidateQueries({ queryKey: ["notifications"] }),
      qc.invalidateQueries({ queryKey: ["effective-permissions"] }),
      qc.invalidateQueries({ queryKey: ["users"] }),
    ]);
  };

  const deleteRowsMutation = useMutation({
    mutationFn: async () => {
      if (!selectedItem) throw new Error("Select a storage category.");
      return deleteStorageRows(selectedItem.key, buildCleanupBody(cleanupMode, beforeDate, startDate, endDate, recordCount), managedTenantId);
    },
    onSuccess: async (result) => {
      setToast(result.message);
      await invalidateAll();
      await detailQuery.refetch();
    },
  });
  const exportDeleteMutation = useMutation({
    mutationFn: async () => {
      if (!selectedItem) throw new Error("Select a storage category.");
      await exportDeleteStorageRows(selectedItem.key, buildCleanupBody(cleanupMode, beforeDate, startDate, endDate, recordCount), managedTenantId);
    },
    onSuccess: async () => {
      setToast("Export generated and matching records deleted.");
      await invalidateAll();
      await detailQuery.refetch();
    },
  });
  const deleteArtifactsMutation = useMutation({
    mutationFn: async () => {
      if (!selectedItem) throw new Error("Select a storage category.");
      return deleteStorageArtifacts(selectedItem.key, selectedArtifactIds, managedTenantId);
    },
    onSuccess: async (result) => {
      setToast(result.message);
      setSelectedArtifactIds([]);
      await invalidateAll();
      await detailQuery.refetch();
    },
  });
  const updateStorageLimitMutation = useMutation({
    mutationFn: async () => {
      if (!editingAdmin?.admin_user_id) throw new Error("Select an admin.");
      const nextLimit = Number(storageLimitDraft || "0");
      if (!nextLimit || nextLimit < 1) throw new Error("Storage limit must be at least 1 GB.");
      return updateUser(editingAdmin.admin_user_id, { storage_limit_gb: nextLimit });
    },
    onSuccess: async () => {
      setToast("Admin storage limit updated.");
      setEditingAdmin(null);
      await invalidateAll();
    },
  });

  const usage = usageQuery.data;
  const items = itemsQuery.data ?? [];
  const detailEntries = detailQuery.data?.entries ?? [];
  const canRunSelectorActions = selectedItem?.supports_export_delete ?? false;
  const canRunArtifactDelete = selectedItem?.supports_simple_delete ?? false;
  const showSelectorDelete = canRunSelectorActions && canDeleteStorage;
  const showSelectorExportDelete = canRunSelectorActions && canDeleteStorage && canExportStorage;
  const showArtifactDelete = canRunArtifactDelete && canDeleteStorage;

  const manageDescription = useMemo(() => {
    if (!selectedItem) return "";
    if (selectedItem.key === "telemetry" || selectedItem.key === "alerts") {
      return "Choose a cutoff strategy, then delete or export and delete matching records.";
    }
    if (selectedItem.key === "ota_files") {
      return "Delete stored firmware files that are not tied to active OTA jobs or campaigns.";
    }
    return "Delete stored device images or dashboard image assets.";
  }, [selectedItem]);

  const rows = superAdminListQuery.data?.items ?? [];
  const totalRows = superAdminListQuery.data?.total ?? 0;
  const canGoPrev = page > 0;
  const canGoNext = (page + 1) * pageSize < totalRows;
  const managedWorkspaceOptions = useMemo(
    () => (usage?.workspace_ids ?? []).map((id, index) => ({ id, name: usage?.workspace_names?.[index] ?? id })),
    [usage?.workspace_ids, usage?.workspace_names],
  );

  const openManagedTenant = (row: SuperAdminAdminStorageRowDto) => {
    const next = new URLSearchParams(searchParams);
    next.set("tenant_id", row.tenant_id);
    if (row.admin_user_id) next.set("admin_user_id", row.admin_user_id);
    setSearchParams(next);
  };

  const closeManagedTenant = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("tenant_id");
    next.delete("admin_user_id");
    setSearchParams(next);
  };

  const openEditLimit = (row: SuperAdminAdminStorageRowDto) => {
    setEditingAdmin(row);
    setStorageLimitDraft(String(Math.max(1, Math.round(row.storage_limit_gb || 0))));
  };

  if (isSuperAdminListMode) {
    return (
      <EnterprisePageShell hideHeader title="Data Management" description="Review admin storage consumption, quota state, and workspace cleanup entry points.">
        <div className="grid gap-5 p-5">
          <ListSurface>
            <ListSurfaceHeader
              title="Admin storage directory"
              description="Monitor admin-owned workspace usage and open cleanup controls."
              meta={<ListCount total={totalRows} noun={totalRows === 1 ? "admin" : "admins"} />}
              controls={(
                <ListToolbar>
                  <ListToolbarGroup grow>
                    <div className="relative min-w-[240px]">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
                      <input
                      className="ui-control h-10 w-full rounded-control bg-surface-primary pl-9 pr-3 text-caption"
                        value={search}
                        onChange={(event) => {
                          setSearch(event.target.value);
                          setPage(0);
                        }}
                        placeholder="Search admin or workspace"
                      />
                    </div>
                  </ListToolbarGroup>
                  <ListToolbarGroup>
                    <select
                      className="ui-control h-10 rounded-control bg-surface-primary px-3 text-caption"
                      value={statusFilter}
                      onChange={(event) => {
                        const value = event.target.value;
                        setStatusFilter(value);
                        setPage(0);
                        const next = new URLSearchParams(searchParams);
                        if (value === "all") next.delete("status");
                        else next.set("status", value);
                        setSearchParams(next);
                      }}
                    >
                      <option value="all">All statuses</option>
                      <option value="normal">Normal</option>
                      <option value="warning">Warning</option>
                      <option value="locked">Locked</option>
                    </select>
                  </ListToolbarGroup>
                </ListToolbar>
              )}
            />
            <ListSurfaceBody className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-card-border text-left text-xs uppercase tracking-[0.12em] text-muted">
                    <th className="px-4 py-3">Admin name</th>
                    <th className="px-4 py-3">Workspaces</th>
                    <th className="px-4 py-3">Storage used</th>
                    <th className="px-4 py-3">Storage limit</th>
                    <th className="px-4 py-3">Usage %</th>
                    <th className="px-4 py-3">Quota status</th>
                    <th className="px-4 py-3">Assigned data loggers</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {superAdminListQuery.isLoading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-4">
                        {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={8} />)}
                      </td>
                    </tr>
                  ) : rows.length ? rows.map((row) => (
                    <tr key={row.admin_user_id ?? row.tenant_id} className="border-b border-card-border/60 last:border-b-0">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{row.admin_name || "Unknown admin"}</div>
                        <div className="text-xs text-muted">{row.admin_email || row.tenant_name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-foreground">{row.workspace_names.join(", ")}</div>
                        <div className="text-xs text-muted">{row.workspace_names.length} workspace{row.workspace_names.length === 1 ? "" : "s"}</div>
                      </td>
                      <td className="px-4 py-3 font-medium text-foreground">{formatBytes(row.total_bytes)}</td>
                      <td className="px-4 py-3 text-foreground">{row.storage_limit_label}</td>
                      <td className="px-4 py-3 text-foreground">{row.usage_percent.toFixed(2)}%</td>
                      <td className="px-4 py-3">
                        <span className={quotaTone(row.quota_status)}>
                          {row.quota_status === "locked" ? "Locked" : row.quota_status === "warning" ? "Warning" : "Normal"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground">{row.assigned_devices_count}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="secondary" size="sm" onClick={() => openManagedTenant(row)}>
                            Manage storage
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => openEditLimit(row)} disabled={!row.admin_user_id}>
                            <Pencil className="h-4 w-4" aria-hidden />
                            Edit limit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted">
                        No admins match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </ListSurfaceBody>
            <ListSurfaceFooter
              left={totalRows ? `Showing ${page * pageSize + 1} to ${Math.min((page + 1) * pageSize, totalRows)} of ${totalRows}` : "No admins found"}
              right={(
                <>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setPage((current) => Math.max(0, current - 1))} disabled={!canGoPrev}>
                    Previous
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setPage((current) => current + 1)} disabled={!canGoNext}>
                    Next
                  </Button>
                </>
              )}
            />
          </ListSurface>
        </div>

        <Modal
          open={Boolean(editingAdmin)}
          onClose={() => setEditingAdmin(null)}
          title={editingAdmin ? `Edit storage limit for ${editingAdmin.admin_name || editingAdmin.tenant_name}` : "Edit storage limit"}
          description="Update the assigned storage quota for this workspace owner admin."
          footer={(
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditingAdmin(null)}>Cancel</Button>
              <Button type="button" size="sm" onClick={() => void updateStorageLimitMutation.mutate()} disabled={updateStorageLimitMutation.isPending}>
                Save
              </Button>
            </div>
          )}
        >
          <label className="grid gap-2 text-sm">
            <span className="text-xs font-medium text-muted">Storage limit in GB</span>
            <input
              className="rounded-control border border-card-border bg-surface-primary px-3 py-2"
              type="number"
              min={1}
              value={storageLimitDraft}
              onChange={(event) => setStorageLimitDraft(event.target.value.replace(/\D/g, ""))}
            />
          </label>
        </Modal>

        {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
      </EnterprisePageShell>
    );
  }

  return (
    <EnterprisePageShell hideHeader title="Data Management" description="Review storage usage, quota state, and cleanup controls.">
      <div className="grid gap-5 p-5">
        {superAdmin && managedTenantId ? (
          <div className="flex items-center justify-between gap-3 rounded-card border border-card-border bg-surface-primary px-4 py-3 shadow-raised">
            <div>
              <div className="text-sm font-semibold text-foreground">{usage?.admin_name || "Workspace storage manager"}</div>
              <div className="text-xs text-muted">
                {usage?.workspace_count && usage.workspace_count > 1
                  ? `${usage.workspace_count} owner workspaces`
                  : usage?.tenant_name || managedTenantId}
              </div>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={closeManagedTenant}>
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back to admins
            </Button>
          </div>
        ) : null}

        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          {[
            ["Total used", usage ? formatBytes(usage.total_bytes) : "--"],
            ["Telemetry", usage ? formatBytes(usage.telemetry_bytes) : "--"],
            ["Alerts", usage ? formatBytes(usage.alerts_bytes) : "--"],
            ["OTA files", usage ? formatBytes(usage.ota_files_bytes) : "--"],
            ["Images & media", usage ? formatBytes(usage.media_bytes) : "--"],
            ["Backups", usage ? formatBytes(usage.backups_bytes) : "--"],
          ].map(([label, value]) => (
            <div key={label} className="rounded-card border border-card-border bg-surface-primary p-4 shadow-raised">
              <div className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">{label}</div>
              <div className="mt-2 text-2xl font-bold text-foreground">{value}</div>
            </div>
          ))}
        </div>

        <ListSurface>
          <ListSurfaceHeader>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Storage limit</h2>
              <p className="mt-1 text-xs text-muted">Quota applies to telemetry, alerts, OTA firmware, and device or dashboard image media. Older data before the quota cutoff remains visible.</p>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-foreground">
                {usage ? `${usage.usage_percent}% of ${usage.storage_limit_label}` : "Loading..."}
              </div>
              <div className="text-xs text-muted">
                {usage ? `${usage.assigned_devices_count} assigned data loggers${usage.quota_locked ? " - quota lock active" : ""}` : ""}
              </div>
            </div>
          </ListSurfaceHeader>
          <ListSurfaceBody>
            {superAdmin && managedAdminUserId && managedWorkspaceOptions.length > 1 ? (
              <div className="border-b border-card-border px-4 py-4">
                <label className="grid max-w-sm gap-2 text-sm">
                  <span className="text-xs font-medium text-muted">Workspace for cleanup actions</span>
                  <select
                    className="rounded-control border border-card-border bg-surface-primary px-3 py-2"
                    value={managedTenantId ?? managedWorkspaceOptions[0]?.id ?? ""}
                    onChange={(event) => {
                      const next = new URLSearchParams(searchParams);
                      next.set("tenant_id", event.target.value);
                      if (managedAdminUserId) next.set("admin_user_id", managedAdminUserId);
                      setSearchParams(next);
                    }}
                  >
                    {managedWorkspaceOptions.map((workspace) => (
                      <option key={workspace.id} value={workspace.id}>{workspace.name}</option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-card-border text-left text-xs uppercase tracking-[0.12em] text-muted">
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Usage</th>
                    <th className="px-4 py-3">Count</th>
                    <th className="px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.key} className="border-b border-card-border/60 last:border-b-0">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{item.label}</div>
                        <div className="text-xs text-muted">
                          {item.supports_export_delete ? "Delete or export and delete" : "Simple delete"}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-foreground">{formatBytes(item.bytes_used)}</td>
                      <td className="px-4 py-3 text-muted">{item.record_count || item.item_count}</td>
                      <td className="px-4 py-3">
                        {canManageStorage ? (
                          <Button type="button" variant="secondary" size="sm" onClick={() => { setSelectedItem(item); setSelectedArtifactIds([]); }}>
                            Manage
                          </Button>
                        ) : (
                          <span className="text-xs text-muted">View only</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ListSurfaceBody>
        </ListSurface>

        <ListSurface>
          <ListSurfaceHeader>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Controls</h2>
              <p className="mt-1 text-xs text-muted">Use device row actions for backup or backup-and-delete. Use the storage manager below for workspace-wide cleanup.</p>
            </div>
          </ListSurfaceHeader>
          <ListSurfaceBody>
            <div className="grid gap-4 p-4 md:grid-cols-3">
              <div className="flex gap-3 rounded-card border border-card-border bg-surface-primary p-4">
                <Database className="mt-1 h-5 w-5 text-primary" />
                <div>
                  <div className="text-sm font-semibold text-foreground">Quota guard</div>
                  <p className="mt-1 text-xs text-muted">Once the limit is consumed, reads, alerts, and new media uploads stop for post-cutoff data only.</p>
                </div>
              </div>
              <div className="flex gap-3 rounded-card border border-card-border bg-surface-primary p-4">
                <Download className="mt-1 h-5 w-5 text-primary" />
                <div>
                  <div className="text-sm font-semibold text-foreground">Export and delete</div>
                  <p className="mt-1 text-xs text-muted">Telemetry and alert history can be exported and deleted by date window or oldest-record count.</p>
                </div>
              </div>
              <div className="flex gap-3 rounded-card border border-card-border bg-surface-primary p-4">
                <Trash2 className="mt-1 h-5 w-5 text-primary" />
                <div>
                  <div className="text-sm font-semibold text-foreground">Artifact cleanup</div>
                  <p className="mt-1 text-xs text-muted">OTA binaries and image assets can be removed directly. Active OTA files are protected from deletion.</p>
                </div>
              </div>
            </div>
            {!canManageStorage ? (
              <div className="border-t border-card-border px-4 py-4 text-xs text-muted">
                Your access is view-only. A workspace admin can grant `delete` and `export` actions for `Data Management` from the permissions module.
              </div>
            ) : null}
          </ListSurfaceBody>
        </ListSurface>
      </div>

      <Modal
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        title={selectedItem ? `Manage ${selectedItem.label}` : "Manage storage"}
        description={manageDescription}
        size="lg"
        footer={
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-muted">
              {selectedItem ? `Current usage: ${formatBytes(selectedItem.bytes_used)}` : ""}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedItem(null)}>Close</Button>
              {showSelectorDelete ? (
                <>
                  {showSelectorExportDelete ? (
                    <Button type="button" variant="secondary" size="sm" onClick={() => void exportDeleteMutation.mutate()} disabled={exportDeleteMutation.isPending || deleteRowsMutation.isPending}>
                      Export + delete
                    </Button>
                  ) : null}
                  <Button type="button" size="sm" onClick={() => void deleteRowsMutation.mutate()} disabled={deleteRowsMutation.isPending || exportDeleteMutation.isPending}>
                    Delete
                  </Button>
                </>
              ) : null}
              {showArtifactDelete ? (
                <Button type="button" size="sm" onClick={() => void deleteArtifactsMutation.mutate()} disabled={!selectedArtifactIds.length || deleteArtifactsMutation.isPending}>
                  Delete selected
                </Button>
              ) : null}
            </div>
          </div>
        }
      >
        {showSelectorDelete ? (
          <div className="grid gap-4">
            <div className="grid gap-3 md:grid-cols-4">
              <label className="grid gap-1 text-sm">
                <span className="text-xs font-medium text-muted">Mode</span>
                <select className="ui-control rounded-control bg-surface-primary px-3 py-2" value={cleanupMode} onChange={(event) => setCleanupMode(event.target.value as StorageCleanupRequestDto["mode"])}>
                  <option value="before_date">Before date</option>
                  <option value="date_range">Between dates</option>
                  <option value="oldest_records">Oldest records</option>
                </select>
              </label>
              {cleanupMode === "before_date" ? (
                <label className="grid gap-1 text-sm">
                  <span className="text-xs font-medium text-muted">Before date</span>
                  <input className="ui-control rounded-control bg-surface-primary px-3 py-2" type="date" value={beforeDate} onChange={(event) => setBeforeDate(event.target.value)} />
                </label>
              ) : null}
              {cleanupMode === "date_range" ? (
                <>
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs font-medium text-muted">Start date</span>
                    <input className="ui-control rounded-control bg-surface-primary px-3 py-2" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
                  </label>
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs font-medium text-muted">End date</span>
                    <input className="ui-control rounded-control bg-surface-primary px-3 py-2" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
                  </label>
                </>
              ) : null}
              {cleanupMode === "oldest_records" ? (
                <label className="grid gap-1 text-sm">
                  <span className="text-xs font-medium text-muted">Oldest record count</span>
                  <input className="ui-control rounded-control bg-surface-primary px-3 py-2" type="number" min={1} value={recordCount} onChange={(event) => setRecordCount(event.target.value.replace(/\D/g, ""))} />
                </label>
              ) : null}
            </div>
            <div className="rounded-control border border-card-border bg-surface-secondary p-4 text-micro text-muted">
              First breach data remains visible before cutoff. Cleanup removes stored records and can unlock future reads after usage falls below quota.
            </div>
          </div>
        ) : null}

        {showArtifactDelete ? (
          <div className="grid gap-3">
            {detailQuery.isLoading ? <div className="text-sm text-muted">Loading entries...</div> : null}
            {!detailQuery.isLoading && !detailEntries.length ? <div className="text-sm text-muted">No stored entries found.</div> : null}
            {detailEntries.map((entry) => {
              const id = entryId(entry.id);
              const checked = selectedArtifactIds.includes(id);
              const bytesUsed = typeof entry.bytes_used === "number" ? entry.bytes_used : 0;
              const inUse = Boolean(entry.in_use);
              return (
                <label key={id} className="flex items-start gap-3 rounded-lg border border-card-border p-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={inUse}
                    onChange={(event) => {
                      setSelectedArtifactIds((current) => event.target.checked ? [...current, id] : current.filter((value) => value !== id));
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      {actionIcon(selectedItem?.key ?? "")}
                      <span className="truncate">{String(entry.label ?? entry.file_name ?? entry.path ?? id)}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted">
                      {typeof entry.path === "string" ? entry.path : typeof entry.version === "string" ? `Version ${entry.version}` : ""}
                    </div>
                    <div className="mt-1 text-xs text-muted">
                      {formatBytes(bytesUsed)}{inUse ? " - deletion blocked while OTA is active" : ""}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        ) : null}
      </Modal>

      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </EnterprisePageShell>
  );
}

function buildCleanupBody(
  mode: StorageCleanupRequestDto["mode"],
  beforeDate: string,
  startDate: string,
  endDate: string,
  recordCount: string,
): StorageCleanupRequestDto {
  if (mode === "before_date") {
    return { mode, before_date: beforeDate || null };
  }
  if (mode === "date_range") {
    return { mode, start_date: startDate || null, end_date: endDate || null };
  }
  return { mode, record_count: Number(recordCount || "0") || null };
}
